import { Box } from '@radix-ui/themes'
import cn from 'clsx'
import { useKeenSlider } from 'keen-slider/react.js'
import * as React from 'react'

import { ProductGridItem } from '#app/components/common/product-grid-item'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product'
import { type GetProducts } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-products'

/* -------------------------------------------------------------------------------------------------
 * ProductsSlider
 * -----------------------------------------------------------------------------------------------*/

enum TEST_ID {
	'products-slider' = 'products-slider',
}

type ProductsSliderProps = {
	products: GetProducts
}

const ProductsSlider: React.FC<ProductsSliderProps> = ({ products }) => {
	const animation = React.useMemo(
		() => ({ duration: 100000, easing: (t: number) => t }),
		[],
	)
	const [isMounted, setIsMounted] = React.useState(false)
	const items = React.useRef(products?.items || [])

	const [ref, slider] = useKeenSlider<HTMLDivElement>({
		loop: true,
		renderMode: 'performance',
		mode: 'free',
		slides: {
			origin: 'center',
			perView: () => Math.min(window.innerWidth / 450, 2.5),
			spacing: 20,
		},
		created(s) {
			setIsMounted(true)
			s.moveToIdx(5, true, animation)
		},
		updated(s) {
			s.moveToIdx(s.track.details.abs + 5, true, animation)
		},
		animationEnded(s) {
			s.moveToIdx(s.track.details.abs + 5, true, animation)
		},
	})

	const handleMouseEnter = React.useCallback(() => {
		if (slider.current) {
			slider.current.animator.stop()
		}
	}, [slider])

	const handleMouseLeave = React.useCallback(() => {
		if (slider.current) {
			slider.current.moveToIdx(
				slider.current.track.details.abs + 5,
				true,
				animation,
			)
		}
	}, [animation, slider])

	return products?.items?.length ? (
		<>
			{products?.items?.length && (
				<Box
					data-testid={TEST_ID['products-slider']}
					position="relative"
					py="9"
					ref={ref}
					className={cn(
						'rounded opacity-0 transition-opacity duration-300',
						{
							'opacity-100': isMounted,
						},
						'keen-slider',
					)}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					{
						// @ts-ignore
						items.current.map((product: GetProduct) => (
							<ul key={product.id} className="list-none">
								<ProductGridItem
									key={product.id}
									product={product}
									inWishlist={false}
									displayWishlist={false}
									className="keen-slider__slide"
								/>
							</ul>
						))
					}
				</Box>
			)}
		</>
	) : <></>
}
ProductsSlider.displayName = 'ProductsSlider'

/* -----------------------------------------------------------------------------------------------*/

export { ProductsSlider, TEST_ID as PRODUCTS_SLIDER_TEST_ID }
